import { cn } from "@/lib/utils";
import { tsToLocaleDateTimeString } from "@/lib/dateTime";
import { PlayerHistoryItem, PlayerModerationState } from "@shared/playerApiTypes";
import InlineCode from "@/components/InlineCode";
import { ModalTabInner, ModalTabMessage } from "@/components/modal-tabs";


type HistoryItemProps = {
    action: PlayerHistoryItem,
    serverTime: number,
}

function HistoryItem({ action, serverTime }: HistoryItemProps) {
    let footerNote, borderColorClass, actionMessage;
    if (action.type === 'ban') {
        borderColorClass = 'border-destructive';
        actionMessage = `BANNED by ${action.author}`;
    } else if (action.type === 'warn') {
        borderColorClass = 'border-warning';
        actionMessage = `WARNED by ${action.author}`;
    } else {
        borderColorClass = 'border-primary/60';
        actionMessage = `NOTE by ${action.author}`;
    }
    if (action.revoked) {
        borderColorClass = '';
        footerNote = action.revokedAt
            ? `Revoked on ${tsToLocaleDateTimeString(action.revokedAt, 'medium', 'short')}.`
            : 'Revoked in FC Admin.';
    } else if (typeof action.exp === 'number') {
        const expirationDate = tsToLocaleDateTimeString(action.exp, 'medium', 'short');
        footerNote = (action.exp < serverTime) ? `Expired on ${expirationDate}.` : `Expires in ${expirationDate}.`;
    }

    return (
        <div
            className={cn(
                'pl-2 border-l-4 rounded-r-sm bg-muted/30',
                borderColorClass
            )}
        >
            <div className="flex w-full justify-between">
                <strong className="text-sm text-muted-foreground">{actionMessage}</strong>
                <small className="text-right text-2xs space-x-1">
                    <InlineCode className="tracking-widest">{action.id}</InlineCode>
                    <span
                        className="opacity-75 cursor-help"
                        title={tsToLocaleDateTimeString(action.ts, 'long', 'long')}
                    >
                        {tsToLocaleDateTimeString(action.ts, 'medium', 'short')}
                    </span>
                </small>
            </div>
            <span className="text-sm">{action.reason}</span>
            {footerNote && <small className="block text-xs opacity-75">{footerNote}</small>}
        </div>
    );
}


type PlayerHistoryTabProps = {
    actionHistory: PlayerHistoryItem[],
    serverTime: number,
    moderation: PlayerModerationState,
}

export default function PlayerHistoryTab({ actionHistory, serverTime, moderation }: PlayerHistoryTabProps) {
    if (!moderation.available) {
        return <ModalTabMessage>
            Diese txAdmin-Ansicht zeigt bewusst keine veraltete Legacy-Historie. Bitte die FC-Spielerakte öffnen.
        </ModalTabMessage>;
    }

    if (!actionHistory.length) {
        return <ModalTabMessage>
            Keine Bans, Verwarnungen oder Notizen in FC Admin gefunden.
        </ModalTabMessage>;
    }

    const reversedActionHistory = [...actionHistory].reverse();
    return (
        <ModalTabInner className="flex flex-col gap-1">
            <div className="mb-1 text-xs text-muted-foreground">Verbindliche Quelle: FC Admin</div>
            {reversedActionHistory.map((action) => (
                <HistoryItem
                    key={`${action.type}-${action.id}`}
                    action={action}
                    serverTime={serverTime}
                />
            ))}
        </ModalTabInner>
    );
}
